import { BrandPalette } from '@tcl-boron-colors/colors';
import { MaxPageWidth } from '~/components/_common/MaxPageWidth';
import React from 'react';
import { useLocation } from 'react-router-dom';

import paths, { checkPath } from '~/routes/paths';
import { useDispatch, useSelector } from '~/store';
import { creators as siteCreators, isUserSitesPending } from '~/store/site';

import SiteSelectionHeaderContainer from '~/components/_core/SiteSelectionHeaderContainer';
import { Spinner } from '~/components/_tclOverrides/Spinner/Spinner';
import { allSitesOption } from '~/constants/boron-components';
import { NoResultImageBoron } from '~/components/_common/Images/NoResultImageBoron';
import { NotAvailable } from '~/components/_common/Images/NotAvailable';

const columnStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  width: '100%',
};

const SiteSelection = ({ children }: { children?: React.ReactNode }) => {
  const dispatch = useDispatch();

  const canImpersonate = useSelector(({ user }) => user.canImpersonate);
  const selectedUserSite = useSelector(({ site }) => site.selectedUserSite);
  const userSites = useSelector(({ site }) => site.userSites);
  const userSitesLoading = useSelector(({ site }) => site.loading.userSites);
  const siteError = useSelector(({ site }) => site.error);
  const sitesResolved = userSites != null;
  const sitesPending = isUserSitesPending(userSites, userSitesLoading, siteError);
  const userSiteCount = sitesResolved ? Object.keys(userSites).length : 0;
  const location = useLocation();
  const path = location.pathname;
  const onTrialDocuments = checkPath(path, paths.TRIAL_DOCUMENTS);

  // Hide the header until the site count is known. A cached selection can still render the page.
  const showSiteSelectionHeader =
    sitesResolved && (onTrialDocuments ? !canImpersonate && userSiteCount > 1 : userSiteCount > 1);

  const header = showSiteSelectionHeader ? (
    <div style={{ flexShrink: 0, position: 'relative', width: '100%', zIndex: 2 }}>
      <SiteSelectionHeaderContainer />
    </div>
  ) : null;

  // A cached selection stays visible. Block the page only when nothing is selected yet.
  if (sitesPending && !selectedUserSite) {
    return <Spinner relativePosition loading transparent />;
  }

  if (selectedUserSite && (!sitesResolved || userSiteCount > 0)) {
    return (
      <div style={columnStyle}>
        {header}
        {children}
      </div>
    );
  }

  if (userSiteCount === 0) {
    return <div></div>;
  } else if (showSiteSelectionHeader) {
    return (
      <div style={columnStyle}>
        {header}
        <MaxPageWidth>
          <div style={{ width: '100%', color: BrandPalette[700] }}>
            <NotAvailable
              title={'No site selected'}
              Image={NoResultImageBoron}
              description={
                'Select a site from the top left dropdown to see all of its available trials and relevant patients.'
              }
            />
          </div>
        </MaxPageWidth>
      </div>
    );
  } else if (canImpersonate) {
    // on pages with no SiteSelectionHeader, internal users get set to "All Sites" if they have no selected site in their local storage
    dispatch(
      siteCreators.setUserSites(userSites, {
        id: allSitesOption.value,
        name: allSitesOption.label,
      }),
    );
    return <div></div>;
  } else {
    return <div></div>;
  }
};

export default SiteSelection;
